import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Note: The happy-path server-side render for /p/:id is now handled by
// `src/setupProxy.js`, which proxies to `/api/public/projects/:id/index.html`
// and returns the fully-crawlable HTML (business SEO + LocalBusiness JSON-LD
// + rendered landing page) BEFORE any JavaScript runs. This React component
// is only reached when the proxy falls through (missing / private / not-yet
// generated). Its only job is the branded "not available" screen.

const PublicPage = () => {
  const { id } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | not_available
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/projects/${id}`, { timeout: 15000 });
        if (cancelled) return;
        setData(res.data);
        setState('ready');
        if (res.data?.name) document.title = `${res.data.name} — YouBuild`;
      } catch (_err) {
        if (!cancelled) setState('not_available');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state === 'loading') {
    return (
      <div
        data-testid="public-page-loading"
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--yb-bg)' }}
      >
        <Loader2 className="animate-spin" style={{ color: 'var(--yb-indigo)' }} />
      </div>
    );
  }

  if (state === 'not_available') {
    return (
      <div
        data-testid="public-page-not-available"
        className="min-h-screen flex items-center justify-center px-6"
        style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}
      >
        <div className="max-w-md w-full text-center">
          <div
            className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: '#EEF2FF', color: 'var(--yb-indigo)' }}
          >
            <AlertCircle size={22} />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
            This page isn’t <span className="italic">available.</span>
          </h1>
          <p className="mt-3 text-[14.5px]" style={{ color: 'var(--yb-muted)' }}>
            The owner may have made it private, or the link is incorrect. If you were expecting to see something here, ask them for an updated link.
          </p>
          <a
            href="/"
            data-testid="public-page-back-home"
            className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold mt-6"
          >
            Back to YouBuild
          </a>
        </div>
      </div>
    );
  }

  // Fallback: if setupProxy fell through for a reason other than 404 (e.g.
  // dev proxy misconfigured in a build target that isn't the CRA dev server),
  // render the generated HTML in a sandboxed iframe so the SPA and its
  // global state (Supabase client, meta tags in <head>) stay untouched.
  return (
    <iframe
      title={data?.name || 'Shared page'}
      srcDoc={data?.generated_html || ''}
      sandbox="allow-scripts"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        border: 0,
        backgroundColor: '#fff',
      }}
    />
  );
};

export default PublicPage;
